import { Avatar, getContrastRatio, useTheme } from "@mui/material";
import * as React from "react";
import { UserView } from "./AccountMenu";
import { TopNavContext } from "./TopNav";
import { AppBarColor, useLocationConfiguration } from "./LocationConfiguration";

export interface UserAvatarProps {
  user: UserView;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ user }) => {
  const inTopNav = React.useContext(TopNavContext);
  const { locationConfiguration } = useLocationConfiguration();
  const { appBarColor } = locationConfiguration.pacSettings.displaySettings;
  const theme = useTheme();

  const styles = React.useMemo(() => {
    if (!inTopNav) {
      return {
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      };
    }

    if (appBarColor === AppBarColor.Primary) {
      return {
        bgcolor: theme.palette.primary.contrastText,
        color: theme.palette.primary.main,
      };
    }

    if (appBarColor === AppBarColor.Secondary) {
      return {
        bgcolor: theme.palette.secondary.contrastText,
        color: theme.palette.secondary.main,
      };
    }

    const defaultBackgroundColor =
      window
        .getComputedStyle(document.body)
        .getPropertyValue("--mui-palette-AppBar-defaultBg")
        .trim() || "#f5f5f5";

    if (!!defaultBackgroundColor) {
      if (
        getContrastRatio(defaultBackgroundColor, theme.palette.primary.main) >=
        3
      ) {
        return {
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        };
      } else if (
        getContrastRatio(defaultBackgroundColor, theme.palette.primary.dark) >=
        3
      ) {
        return {
          bgcolor: theme.palette.primary.dark,
          color: theme.palette.getContrastText(theme.palette.primary.dark),
        };
      } else if (
        getContrastRatio(
          defaultBackgroundColor,
          theme.palette.secondary.main
        ) >= 3
      ) {
        return {
          bgcolor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        };
      } else if (
        getContrastRatio(
          defaultBackgroundColor,
          theme.palette.secondary.dark
        ) >= 3
      ) {
        return {
          bgcolor: theme.palette.secondary.dark,
          color: theme.palette.getContrastText(theme.palette.secondary.dark),
        };
      }
    }

    console.warn(
      "unable to find UserAvatar background color with sufficient contrast"
    );
    // nothing satisfies... stick with the stock MUI colors
    return {};
  }, [
    appBarColor,
    inTopNav,
    theme.palette.primary.main,
    theme.palette.secondary.main,
  ]);

  return (
    <Avatar
      sx={{
        width: 32,
        height: 32,
        ...styles,
      }}
    >{`${(user?.firstName || "").substring(0, 1)}${(
      user?.lastName || ""
    ).substring(0, 1)}`}</Avatar>
  );
};
